import { useMemo, useState } from 'react';
import { Card, ConfirmModal, Field, Modal, Skeleton } from '../../components/UI';
import { useToast } from '../../components/Toast';
import { deleteContact, saveContact } from '../../firebase/repo';
import type { Contact } from '../../types';
import { contactDuplicateKeys, normalizeContactInput } from '../../utils/contacts';
import { getContactImportAvailability, GOOGLE_CONTACTS_IMPORT_ENABLED } from './contactImportFeature';
import { fetchGoogleContactsPreview } from './googleContacts';

type Draft = Partial<Contact>;

export function ContactManager({ uid, contacts }: { uid: string; contacts: Contact[] }) {
  const toast = useToast();
  const importAvailability = getContactImportAvailability();
  const [draft, setDraft] = useState<Draft>({ name: '', phone: '', email: '', source: 'manual', active: true });
  const [formOpen, setFormOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<Array<Partial<Contact> & { selected?: boolean; duplicate?: boolean }>>([]);
  const [csvPreview, setCsvPreview] = useState<Array<Partial<Contact> & { selected?: boolean; duplicate?: boolean }>>([]);
  const [confirmDelete, setConfirmDelete] = useState<Contact | null>(null);

  const existingKeys = useMemo(() => new Set(contacts.flatMap(contactDuplicateKeys)), [contacts]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contacts.filter((contact) => !q || [contact.name, contact.phone, contact.email].some((value) => String(value || '').toLowerCase().includes(q)));
  }, [contacts, query]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await saveContact(uid, { ...draft, source: draft.source || 'manual' });
      setDraft({ name: '', phone: '', email: '', source: 'manual', active: true });
      setFormOpen(false);
      toast('Contact saved');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Contact save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (contact: Contact) => {
    await saveContact(uid, { ...contact, active: !contact.active });
    toast(contact.active === false ? 'Contact activated' : 'Contact deactivated');
  };

  const startImport = async () => {
    if (!GOOGLE_CONTACTS_IMPORT_ENABLED) return;
    setImporting(true);
    try {
      const rows = await fetchGoogleContactsPreview();
      setPreview(rows.map((row) => {
        const duplicate = contactDuplicateKeys(row).some((key) => existingKeys.has(key));
        return { ...row, selected: !duplicate, duplicate };
      }));
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Google Contacts import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  const saveImport = async () => {
    if (saving) return;
    const selected = preview.filter((row) => row.selected && !row.duplicate);
    if (!selected.length) return toast('Select at least one new contact');
    setSaving(true);
    try {
      await Promise.all(selected.map((row) => saveContact(uid, { ...normalizeContactInput(row), source: 'google', importedAt: new Date().toISOString(), active: true })));
      setPreview([]);
      toast(`${selected.length} contact(s) imported`);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Import save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const markDuplicates = (rows: Partial<Contact>[]) => rows.map((row) => {
    const clean = normalizeContactInput(row);
    const duplicate = contactDuplicateKeys(clean).some((key) => existingKeys.has(key));
    return { ...clean, selected: !duplicate, duplicate };
  });

  const downloadCsvTemplate = () => {
    const blob = new Blob(['name,phone,email\nExample Name,+919999999999,example@gmail.com\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pfs-contacts-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const uploadCsv = async (file?: File) => {
    if (!file) return;
    const text = await file.text();
    const [headerLine, ...lines] = text.split(/\r?\n/).filter(Boolean);
    const headers = headerLine.split(',').map((h) => h.trim().toLowerCase());
    const rows = lines.map((line) => {
      const values = line.split(',').map((value) => value.trim());
      const row = Object.fromEntries(headers.map((header, index) => [header, values[index] || '']));
      return { name: row.name || '', phone: row.phone || '', email: row.email || '', source: 'manual' as const, active: true };
    }).filter((row) => row.name || row.phone || row.email);
    setCsvPreview(markDuplicates(rows));
  };

  const saveCsvImport = async () => {
    if (saving) return;
    const selected = csvPreview.filter((row) => row.selected && !row.duplicate);
    if (!selected.length) return toast('Select at least one new CSV contact');
    setSaving(true);
    try {
      await Promise.all(selected.map((row) => saveContact(uid, { ...normalizeContactInput(row), source: 'manual', active: true })));
      setCsvPreview([]);
      toast(`${selected.length} CSV contact(s) imported`);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'CSV import failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    try {
      await deleteContact(uid, confirmDelete.id);
      setConfirmDelete(null);
      toast('Contact deleted');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Contact delete failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startAdd = () => {
    setDraft({ name: '', phone: '', email: '', source: 'manual', active: true });
    setFormOpen(true);
  };

  const startEdit = (contact: Contact) => {
    setDraft(contact);
    setFormOpen(true);
  };

  const closeForm = () => {
    setDraft({ name: '', phone: '', email: '', source: 'manual', active: true });
    setFormOpen(false);
  };

  return <Card className="settings-manager-card">
    <div className="section-title"><div><h2>Contacts</h2><p className="muted">Manual and Google contacts for CR/DR ledger.</p></div><div className="row-actions"><button className="btn small primary" onClick={startAdd}>Add contact</button>{importAvailability.googleEnabled ? <button className="btn small" disabled={importing} onClick={startImport}>{importing ? 'Importing...' : importAvailability.googleLabel}</button> : <span className="status-pill inactive">{importAvailability.googleLabel}</span>}</div></div>
    <p className="muted">CSV import is available below. Google Contacts import is temporarily held for the next update.</p>
    <div className="row-actions"><button className="btn small" onClick={downloadCsvTemplate}>Download CSV template</button><label className="btn small">Upload CSV<input type="file" accept=".csv,text/csv" hidden onChange={(e) => uploadCsv(e.target.files?.[0])} /></label></div>
    <div className="settings-summary-strip">
      <span><b>{contacts.filter((contact) => contact.active !== false).length}</b> active</span>
      <span><b>{contacts.filter((contact) => contact.source === 'google').length}</b> Google</span>
      <span><b>{contacts.length}</b> saved</span>
    </div>
    {formOpen && <div className="settings-form-panel">
      <div className="section-title compact"><div><h3>{draft.id ? 'Edit contact' : 'New contact'}</h3><p className="muted">Saved contacts appear as CR/DR person suggestions.</p></div><button className="btn small" onClick={closeForm}>Close</button></div>
      <div className="grid2">
        <Field label="Name"><input value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
        <Field label="Phone"><input value={draft.phone || ''} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></Field>
      </div>
      <Field label="Email"><input value={draft.email || ''} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field>
      <button className="btn primary wide" disabled={saving || (!draft.name && !draft.phone && !draft.email)} onClick={save}>{saving ? 'Saving...' : draft.id ? 'Update contact' : 'Save contact'}</button>
    </div>}
    <Field label="Search contacts"><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, email" /></Field>
    {filtered.map((contact) => <div className="master-row contact-row" key={contact.id}>
      <div><b>{contact.name || contact.phone || contact.email}</b><span>{contact.phone || '-'} / {contact.email || '-'} / {contact.source || 'manual'}</span></div>
      <div className="row-actions"><button className="btn small" onClick={() => startEdit(contact)}>Edit</button><button className={`status-pill ${contact.active === false ? 'inactive' : 'active'}`} onClick={() => toggle(contact)}>{contact.active === false ? 'Inactive' : 'Active'}</button><button className="btn small danger" onClick={() => setConfirmDelete(contact)}>Del</button></div>
    </div>)}
    {!filtered.length && <p className="empty-state">No contacts found.</p>}

    <Modal open={!!preview.length || importing} title="Google Contacts preview" onClose={() => setPreview([])}>
      {importing && <Skeleton />}
      {!importing && preview.map((row, index) => <label className={`import-row ${row.duplicate ? 'duplicate' : ''}`} key={`${row.normalizedPhone || row.normalizedEmail || index}`}>
        <input type="checkbox" disabled={row.duplicate} checked={!!row.selected} onChange={(e) => setPreview((old) => old.map((item, i) => i === index ? { ...item, selected: e.target.checked } : item))} />
        <span><b>{row.name || row.phone || row.email}</b><small>{row.phone || '-'} / {row.email || '-'}{row.duplicate ? ' / already exists' : ''}</small></span>
      </label>)}
      {!importing && <button className="btn primary wide" disabled={saving} onClick={saveImport}>{saving ? 'Saving...' : 'Save selected contacts'}</button>}
    </Modal>
    <Modal open={!!csvPreview.length} title="CSV contacts preview" onClose={() => setCsvPreview([])}>
      {csvPreview.map((row, index) => <label className={`import-row ${row.duplicate ? 'duplicate' : ''}`} key={`${row.normalizedPhone || row.normalizedEmail || index}`}>
        <input type="checkbox" disabled={row.duplicate} checked={!!row.selected} onChange={(e) => setCsvPreview((old) => old.map((item, i) => i === index ? { ...item, selected: e.target.checked } : item))} />
        <span><b>{row.name || row.phone || row.email}</b><small>{row.phone || '-'} / {row.email || '-'}{row.duplicate ? ' / already exists' : ''}</small></span>
      </label>)}
      <button className="btn primary wide" disabled={saving} onClick={saveCsvImport}>{saving ? 'Saving...' : 'Save selected CSV contacts'}</button>
    </Modal>
    <ConfirmModal open={!!confirmDelete} title="Delete contact?" message="This removes the saved contact. Existing CR/DR entries will still keep their stored person name." tone="danger" confirmLabel="Delete" loading={saving} details={confirmDelete ? [{ label: 'Name', value: confirmDelete.name || '-' }, { label: 'Phone', value: confirmDelete.phone || '-' }, { label: 'Email', value: confirmDelete.email || '-' }] : []} onCancel={() => setConfirmDelete(null)} onConfirm={remove} />
  </Card>;
}
